/**
 * The /overview filter bar (spec §3.4): the D1 period presets (Bangkok,
 * ending yesterday; custom = two dates under the server's own rules), the
 * remembered channel dropdown, the objective, and the currency — shown only
 * when there is more than one, while unknown-currency spend is flagged even
 * when there is one (R8). Styled like the /looker band. The Channel Deep-dive
 * reuses it (its R20): `channelFilter={false}` drops the channel dropdown, the
 * page being one channel, and `children` (Campaign type, Campaign) follow the
 * currency.
 *
 * @module components/overview/OverviewFilterBar
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChannelFilter } from '@/components/ChannelFilter';
import { CurrencyPicker } from '@/components/looker/CurrencyPicker';
import { DATE_OPTIONS, LOOKER_ACTION_CLASS, LOOKER_FIELD_CLASS, LOOKER_LABEL_CLASS } from '@/components/looker/LookerFilterBar';
import { addDays, bangkokToday, customToken, isValidCustomRange } from '@/services/looker/lookerDateRange';
import { OBJECTIVE_FILTERS, type CurrencyChoice, type ObjectiveFilter } from '@/services/overview/overviewTypes';

/** The D1 presets: the /looker list without its all-time entry (value ''). */
const PRESETS = DATE_OPTIONS.filter((o) => o.value !== '');

/** Literal keys, so the locale integrity test sees them. */
const OBJECTIVE_LABEL: Record<ObjectiveFilter, (t: TFunction) => string> = {
  all: (t) => t('looker.objectives.all'),
  awareness: (t) => t('looker.objectives.awareness'),
  consideration: (t) => t('looker.objectives.consideration'),
  conversion: (t) => t('looker.objectives.conversion'),
};

interface Props {
  token: string;
  onToken: (token: string) => void;
  objective: ObjectiveFilter;
  onObjective: (o: ObjectiveFilter) => void;
  /** null until the facts are read. */
  money: CurrencyChoice | null;
  onCurrency: (currency: string) => void;
  disabled?: boolean;
  /** false drops the remembered channel dropdown (the Channel Deep-dive is one channel). Default true. */
  channelFilter?: boolean;
  /** More controls, drawn after the currency. */
  children?: React.ReactNode;
}

export const OverviewFilterBar: React.FC<Props> = ({
  token, onToken, objective, onObjective, money, onCurrency, disabled = false, channelFilter = true, children,
}) => {
  const { t } = useTranslation();
  const isCustom = token.startsWith('custom:');
  const [customOpen, setCustomOpen] = useState(isCustom);
  const [from, setFrom] = useState(isCustom ? token.split(':')[1] ?? '' : '');
  const [to, setTo] = useState(isCustom ? token.split(':')[2] ?? '' : '');
  const today = bangkokToday();
  const yesterday = addDays(today, -1);
  const showCustom = customOpen || isCustom;
  return (
    <div data-overview-filters className="flex flex-wrap items-end gap-3">
      <label className={LOOKER_LABEL_CLASS}>
        {t('overview.filters.period')}
        <select
          aria-label={t('overview.filters.period')}
          className={LOOKER_FIELD_CLASS}
          value={showCustom ? 'custom' : token}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              setCustomOpen(true);
              return;
            }
            setCustomOpen(false);
            onToken(e.target.value);
          }}
        >
          {PRESETS.map((o) => <option key={o.value} value={o.value}>{o.label(t)}</option>)}
        </select>
      </label>
      {showCustom && (
        <>
          <label className={LOOKER_LABEL_CLASS}>
            {t('looker.dateFrom')}
            <input type="date" aria-label={t('looker.dateFrom')} className={LOOKER_FIELD_CLASS} max={yesterday} value={from}
              onChange={(e) => setFrom(e.target.value)} disabled={disabled} />
          </label>
          <label className={LOOKER_LABEL_CLASS}>
            {t('looker.dateTo')}
            <input type="date" aria-label={t('looker.dateTo')} className={LOOKER_FIELD_CLASS} max={yesterday} value={to}
              onChange={(e) => setTo(e.target.value)} disabled={disabled} />
          </label>
          <button type="button" className={LOOKER_ACTION_CLASS} disabled={disabled || !isValidCustomRange(from, to, today)}
            onClick={() => onToken(customToken(from, to))}>
            {t('looker.applyDates')}
          </button>
        </>
      )}
      {channelFilter && <ChannelFilter disabled={disabled} />}
      <label className={LOOKER_LABEL_CLASS}>
        {t('overview.filters.objective')}
        <select
          aria-label={t('overview.filters.objective')}
          className={LOOKER_FIELD_CLASS}
          value={objective}
          disabled={disabled}
          onChange={(e) => onObjective(OBJECTIVE_FILTERS.find((o) => o === e.target.value) ?? 'all')}
        >
          {OBJECTIVE_FILTERS.map((o) => <option key={o} value={o}>{OBJECTIVE_LABEL[o](t)}</option>)}
        </select>
      </label>
      {money && (
        <CurrencyPicker currencies={money.currencies} value={money.currency} unknownExcluded={money.unknownExcluded} onChange={onCurrency} disabled={disabled} />
      )}
      {money && money.unknownExcluded && money.currencies.length <= 1 && (
        <p className="self-center text-xs text-neutral-700">{t('looker.currency.unknownExcluded')}</p>
      )}
      {children}
    </div>
  );
};
